import { MenuItemOfBag } from '@app/contexts/Bag';
import { AddressStorage, UserStorage } from '@app/services/storage';
import { CliqueiAcheiData, DeliveryTypesAvailable, Restaurant } from '@app/types';
import { MenuItemWithDetails } from '@app/types/menu-item';
import { useMemo } from 'react';
import Cookies from 'universal-cookie';
import { removeAccentuation } from './strings';

export class TagManager {
  static pushMenuItemViewed(menuItem: MenuItemWithDetails, extraData = {}) {
    window.dataLayer?.push({
      event: 'CustomizeProduct',
      event_name: 'CustomizeProduct',
      content_category: menuItem.menuName ?? '',
      content_ids: [menuItem.id],
      content_name: menuItem.nome,
      value: menuItem.preco,
      currency: 'BRL',
      content_type: 'product',
      ...extraData,
    });
  }

  static pushInitiateCheckout(items: MenuItemOfBag[], totalOrder: number, extraData = {}) {
    console.log('Initiate checkout with ' + items.length + ' items');
    window.dataLayer?.push({
      event: 'InitiateCheckout',
      event_name: 'InitiateCheckout',
      content_ids: items.map((item) => item.id),
      content_type: 'product_group',
      content_name: 'Sacola',
      value: totalOrder,
      num_items: items.length,
      currency: 'BRL',
      ...extraData,
    });
  }

  static pushAddToBag(menuItem: MenuItemWithDetails, extraData = {}) {
    window.dataLayer?.push({
      event: 'AddToCart',
      event_name: 'AddToCart',
      content_ids: [menuItem.id],
      content_category: menuItem.menuName ?? '',
      content_name: menuItem.nome,
      value: menuItem.preco,
      currency: 'BRL',
      content_type: 'product',
      ...extraData,
    });
  }

  static pushAddToWishlist(menuItem: MenuItemWithDetails, extraData = {}) {
    window.dataLayer?.push({
      event: 'AddToWishlist',
      event_name: 'AddToWishlist',
      content_ids: [menuItem.id],
      content_category: menuItem.menuName ?? '',
      content_name: menuItem.nome,
      value: menuItem.preco,
      currency: 'BRL',
      content_type: 'product',
      ...extraData,
    });
  }

  static pushPurchase(
    items: MenuItemOfBag[],
    totalOrder: number,
    deliveryType: DeliveryTypesAvailable,
    extraData = {}
  ) {
    window.dataLayer?.push({
      event: 'Purchase',
      event_name: 'Purchase',
      content_ids: items.map((item) => item.id),
      value: totalOrder,
      num_items: items.length,
      content_name: 'Compra',
      currency: 'BRL',
      content_type: 'product',
      delivery_category: deliveryType === DeliveryTypesAvailable.HomeDelivery ? 'home_delivery' : 'in_store',
      ...extraData,
    });
  }

  static pushPageViewed(restaurant: Restaurant, cliqueiAcheiData: CliqueiAcheiData, extraData = {}) {
    window.dataLayer?.push({
      event: 'ViewContent',
      event_name: 'ViewContent',
      content_ids: [restaurant.id],
      content_name: cliqueiAcheiData.NomePuro,
      content_category: cliqueiAcheiData.Nom_Categoria,
      content_type: 'product_group',
      currency: 'BRL',
      ...extraData,
    });
  }

  static pushPaymentInfoAdded(payment: string, totalOrder: number, extraData = {}) {
    window.dataLayer?.push({
      event: 'AddPaymentInfo',
      event_name: 'AddPaymentInfo',
      content_name: payment,
      value: totalOrder,
      currency: 'BRL',
      ...extraData,
    });
  }

  static get Classes() {
    return TagManagerClasses;
  }
}

export class TagManagerInstance {
  private cookies = new Cookies();

  constructor(private localCityUrl: string) {}

  pushMenuItemViewed(menuItem: MenuItemWithDetails) {
    TagManager.pushMenuItemViewed(menuItem, this.defaultExtraData());
  }

  pushInitiateCheckout(items: MenuItemOfBag[], totalOrder: number) {
    TagManager.pushInitiateCheckout(items, totalOrder, this.defaultExtraData());
  }

  pushAddToBag(menuItem: MenuItemWithDetails) {
    TagManager.pushAddToBag(menuItem, this.defaultExtraData());
  }

  pushAddToWishlist(menuItem: MenuItemWithDetails) {
    TagManager.pushAddToWishlist(menuItem, this.defaultExtraData());
  }

  pushPurchase(items: MenuItemOfBag[], totalOrder: number, deliveryType: DeliveryTypesAvailable) {
    TagManager.pushPurchase(items, totalOrder, deliveryType, this.defaultExtraData());
  }

  pushPageViewed(restaurant: Restaurant, cliqueiAcheiData: CliqueiAcheiData) {
    TagManager.pushPageViewed(restaurant, cliqueiAcheiData, this.defaultExtraData());
  }

  pushPaymentInfoAdded(payment: string, totalOrder: number) {
    TagManager.pushPaymentInfoAdded(payment, totalOrder, this.defaultExtraData());
  }

  private defaultExtraData() {
    return {
      ...this.userData(),
      ...this.addressData(),
      ...this.fbpCookie(),
      ...this.fbcCookie(),
      client_user_agent: navigator.userAgent,
      event_time: Date.now(),
      event_source_url: window.location.href,
      action_source: 'website',
      country: 'br',
    };
  }

  private addressData() {
    const address = AddressStorage.getByCityUrl(this.localCityUrl);
    if (!address) return {};
    return {
      ct: removeAccentuation(address.city.Nom_Cidade.toLowerCase().split(' ').join('')),
      st: address.city.Sigla_UF.toLowerCase(),
    };
  }

  private userData() {
    const user = UserStorage.get();
    if (!user) return {};
    return {
      fn: user.name.toLowerCase(),
      ph: '55' + user.cellphone,
      external_id: user.cellphone,
    };
  }

  private fbpCookie() {
    const fbp = this.cookies.get('_fbp');
    return fbp ? { fbp } : {};
  }

  private fbcCookie() {
    const fbc = this.cookies.get('_fbc');
    return fbc ? { fbc } : {};
  }
}

export function useTagManagerInstance(cliqueiAcheiData: CliqueiAcheiData | undefined) {
  const instance = useMemo(() => {
    if (!cliqueiAcheiData) {
      console.warn('It were able create the tag manager instance');
      return null;
    }

    return new TagManagerInstance(cliqueiAcheiData.Url_Cidade);
  }, [cliqueiAcheiData?.Url_Cidade]);

  return instance;
}

enum TagManagerClasses {
  ViewItemMenu = 'view_item_menu',
  AddToWishlist = 'add_to_wishlist_menu',
  AddToCart = 'add_to_cart_menu',
  InitiateCheckout = 'begin_checkout_menu',
  AddPaymentInfo = 'add_payment_info_menu',
  Purchase = 'purchase_menu',
}
